/**
 * The adaptive allocation form's model — pure, so it can be tested without React.
 *
 * The form edits PERCENTAGES (what a person types) and the server takes FRACTIONS (what Core
 * validates). Everything that converts between them, fills defaults, and pre-checks sums lives here,
 * so the page is layout only. The server re-validates everything (Core's `parse_adaptive_allocation`);
 * these checks exist so an obviously broken form says so before a run is queued, not after.
 */

export type Metric = 'upi' | 'sortino' | 'sharpe' | 'cagr';
export type Frequency = 'daily' | 'weekly' | 'monthly';

export interface SignalRow {
  metric: Metric;
  lookbackMonths: number;
  weightPct: number;
}

export interface BoundRow {
  minPct: number;
  maxPct: number;
  enabled: boolean;
}

/** A Composer flat condition — kept verbatim, as the portfolio document stores it. */
export type RegimeCondition = Record<string, unknown>;

export interface PolicyForm {
  signals: SignalRow[];
  riskLookbackMonths: number;
  performancePct: number; // risk share is 100 − this
  bounds: Record<string, BoundRow>;
  useRegime: boolean;
  regimeCondition: RegimeCondition;
  regimeLabels: { whenTrue: string; otherwise: string };
  regimeBounds: Record<string, Record<string, BoundRow>>; // label → sleeve → bounds
  smoothingPct: number;
  minChangePct: number;
  maxStepPct: number;
  frequency: Frequency;
  rotationCostBps: number;
}

export const DEFAULT_SIGNALS: SignalRow[] = [
  { metric: 'upi', lookbackMonths: 3, weightPct: 30 },
  { metric: 'upi', lookbackMonths: 6, weightPct: 25 },
  { metric: 'sortino', lookbackMonths: 6, weightPct: 20 },
  { metric: 'sharpe', lookbackMonths: 3, weightPct: 15 },
  { metric: 'cagr', lookbackMonths: 12, weightPct: 10 },
];

export const SPY_BELOW_200: RegimeCondition = {
  'lhs-fn': 'current-price',
  'lhs-val': 'SPY',
  comparator: 'lt',
  'rhs-fn': 'moving-average-price',
  'rhs-fn-params': { window: 200 },
  'rhs-val': 'SPY',
  'rhs-fixed-value?': false,
};

const OPEN: BoundRow = { minPct: 0, maxPct: 100, enabled: true };

export interface PortfolioDoc {
  name?: string;
  sleeves?: { key: string; name?: string | null }[];
  allocation?: Record<string, unknown> & { type?: string };
}

const round = (value: number) => Math.round(value * 1e6) / 1e6;
const pct = (fraction: unknown, fallback: number) =>
  typeof fraction === 'number' ? round(fraction * 100) : fallback;

function boundsFrom(table: unknown, keys: string[]): Record<string, BoundRow> {
  const source = (table ?? {}) as Record<string, { min?: number; max?: number; enabled?: boolean }>;
  return Object.fromEntries(
    keys.map((k) => [
      k,
      {
        minPct: pct(source[k]?.min, 0),
        maxPct: pct(source[k]?.max, 100),
        enabled: source[k]?.enabled ?? true,
      },
    ]),
  );
}

/**
 * The form for a portfolio: its own adaptive policy when it has one; otherwise the V1 defaults,
 * borrowing a `regime-switch` document's regime condition and branch labels so the regime bounds
 * line up with the benchmark it will be compared against.
 */
export function formFromDocument(doc: PortfolioDoc): PolicyForm {
  const keys = (doc.sleeves ?? []).map((s) => s.key);
  const allocation = (doc.allocation ?? {}) as Record<string, any>;
  const blank = Object.fromEntries(keys.map((k) => [k, { ...OPEN }]));

  if (allocation.type === 'performance-risk-adaptive') {
    const labels = allocation.regime?.labels ?? { 'when-true': 'risk-off', else: 'risk-on' };
    const regimeBounds: PolicyForm['regimeBounds'] = {};
    for (const label of [labels['when-true'], labels.else]) {
      regimeBounds[label] = boundsFrom(allocation.regime_constraints?.[label], keys);
    }
    return {
      signals: (allocation.performance?.signals ?? null)?.map((s: any) => ({
        metric: s.metric,
        lookbackMonths: s.lookback_months,
        weightPct: round(s.weight * 100),
      })) ?? DEFAULT_SIGNALS.map((s) => ({ ...s })),
      riskLookbackMonths: allocation.risk?.lookback_months ?? 6,
      performancePct: pct(allocation.blend?.performance, 40),
      bounds: boundsFrom(allocation.constraints, keys),
      useRegime: Boolean(allocation.regime),
      regimeCondition: allocation.regime?.condition ?? SPY_BELOW_200,
      regimeLabels: { whenTrue: labels['when-true'], otherwise: labels.else },
      regimeBounds,
      smoothingPct: pct(allocation.smoothing?.previous_weight, 70),
      minChangePct: pct(allocation.turnover?.min_change, 5),
      maxStepPct: pct(allocation.turnover?.max_step, 10),
      frequency: allocation.rebalance?.frequency ?? 'daily',
      rotationCostBps: allocation.costs?.strategy_rotation_cost_bps ?? 5,
    };
  }

  const branches = (allocation.branches ?? []) as { id?: string; match?: string }[];
  const whenTrue = branches.find((b) => b.match === 'when-true')?.id ?? 'risk-off';
  const otherwise = branches.find((b) => b.match === 'else')?.id ?? 'risk-on';
  const condition = allocation.type === 'regime-switch' ? allocation.regime?.condition : null;
  return {
    signals: DEFAULT_SIGNALS.map((s) => ({ ...s })),
    riskLookbackMonths: 6,
    performancePct: 40,
    bounds: blank,
    useRegime: Boolean(condition),
    regimeCondition: condition ?? SPY_BELOW_200,
    regimeLabels: { whenTrue, otherwise },
    regimeBounds: {
      [whenTrue]: Object.fromEntries(keys.map((k) => [k, { ...OPEN }])),
      [otherwise]: Object.fromEntries(keys.map((k) => [k, { ...OPEN }])),
    },
    smoothingPct: 70,
    minChangePct: 5,
    maxStepPct: 10,
    frequency: 'daily',
    rotationCostBps: 5,
  };
}

const fraction = (value: number) => round(value / 100);

function boundsJson(table: Record<string, BoundRow>) {
  return Object.fromEntries(
    Object.entries(table).map(([k, b]) => [k, { min: fraction(b.minPct), max: fraction(b.maxPct), enabled: b.enabled }]),
  );
}

/** The `performance-risk-adaptive` allocation block the server takes. */
export function allocationFromForm(form: PolicyForm): Record<string, unknown> {
  const out: Record<string, unknown> = {
    type: 'performance-risk-adaptive',
    performance: {
      signals: form.signals.map((s) => ({
        metric: s.metric,
        lookback_months: s.lookbackMonths,
        weight: fraction(s.weightPct),
      })),
      conversion: 'linear-rank',
    },
    risk: { method: 'equal-risk-contribution', lookback_months: form.riskLookbackMonths },
    blend: { performance: fraction(form.performancePct), risk: fraction(100 - form.performancePct) },
    constraints: boundsJson(form.bounds),
    smoothing: { previous_weight: fraction(form.smoothingPct) },
    turnover: { min_change: fraction(form.minChangePct), max_step: fraction(form.maxStepPct) },
    rebalance: { frequency: form.frequency },
    costs: { strategy_rotation_cost_bps: form.rotationCostBps },
  };
  if (form.useRegime) {
    out.regime = {
      condition: form.regimeCondition,
      labels: { 'when-true': form.regimeLabels.whenTrue, else: form.regimeLabels.otherwise },
    };
    // Regime bounds only for sleeves that differ from wide-open, so an untouched table adds nothing.
    out.regime_constraints = Object.fromEntries(
      Object.entries(form.regimeBounds).map(([label, table]) => [
        label,
        Object.fromEntries(
          Object.entries(table)
            .filter(([, b]) => b.minPct !== 0 || b.maxPct !== 100)
            .map(([k, b]) => [k, { min: fraction(b.minPct), max: fraction(b.maxPct) }]),
        ),
      ]),
    );
  }
  return out;
}

/** Problems the server would refuse, stated before a run is queued. Empty means submit. */
export function formProblems(form: PolicyForm): string[] {
  const problems: string[] = [];
  const signalTotal = form.signals.reduce((sum, s) => sum + s.weightPct, 0);
  if (form.signals.length === 0) problems.push('Add at least one metric to the ensemble.');
  else if (Math.abs(signalTotal - 100) > 1e-6) problems.push(`Metric weights sum to ${round(signalTotal)}%, not 100%.`);
  const seen = new Set<string>();
  for (const s of form.signals) {
    const id = `${s.metric}/${s.lookbackMonths}`;
    if (seen.has(id)) problems.push(`${s.metric.toUpperCase()} over ${s.lookbackMonths} months is listed twice.`);
    seen.add(id);
    if (s.lookbackMonths < 1 || s.lookbackMonths > 60) problems.push('Lookbacks must be 1–60 months.');
  }
  if (form.performancePct < 0 || form.performancePct > 100) problems.push('The performance share must be 0–100%.');
  const check = (table: Record<string, BoundRow>, where: string) => {
    const enabled = Object.values(table).filter((b) => b.enabled);
    if (Object.values(table).some((b) => b.minPct > b.maxPct)) problems.push(`${where}: a floor is above its cap.`);
    const floors = enabled.reduce((sum, b) => sum + b.minPct, 0);
    const caps = enabled.reduce((sum, b) => sum + b.maxPct, 0);
    if (floors > 100 + 1e-6) problems.push(`${where}: floors sum to ${round(floors)}%, above 100%.`);
    if (caps < 100 - 1e-6) problems.push(`${where}: caps sum to ${round(caps)}%, below 100%.`);
  };
  check(form.bounds, 'Sleeve bounds');
  if (Object.values(form.bounds).filter((b) => b.enabled).length < 2) problems.push('At least two sleeves must be enabled.');
  if (form.useRegime) {
    for (const [label, table] of Object.entries(form.regimeBounds)) {
      const merged = Object.fromEntries(
        Object.entries(table).map(([k, b]) => [k, { ...b, enabled: form.bounds[k]?.enabled ?? true }]),
      );
      check(merged, `${label} bounds`);
    }
  }
  if (form.smoothingPct < 0 || form.smoothingPct >= 100) problems.push('Smoothing must be at least 0% and below 100%.');
  if (form.maxStepPct <= 0 || form.maxStepPct > 100) problems.push('The maximum step must be above 0% and at most 100%.');
  if (form.minChangePct < 0 || form.minChangePct >= 100) problems.push('The minimum change must be 0–99%.');
  if (form.rotationCostBps < 0) problems.push('The rotation cost cannot be negative.');
  return problems;
}

/** The Triple Blend's documented bounds, as a one-click example for a three-sleeve portfolio. */
export function tripleBlendExample(form: PolicyForm, keys: string[]): PolicyForm {
  if (keys.length !== 3) return form;
  const [core, alpha, vix] = keys;
  const { whenTrue, otherwise } = form.regimeLabels;
  const row = (minPct: number, maxPct: number): BoundRow => ({ minPct, maxPct, enabled: true });
  return {
    ...form,
    useRegime: true,
    regimeBounds: {
      [otherwise]: { [core]: row(60, 85), [alpha]: row(5, 30), [vix]: row(5, 15) },
      [whenTrue]: { [core]: row(35, 60), [alpha]: row(25, 50), [vix]: row(10, 20) },
    },
  };
}
